import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 8);
};

// Crear enlace seguro
export const createSecureLink = async (linkData) => {
  try {
    const linkId = generateUniqueId();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 horas

    const linkDoc = {
      linkId,
      amount: linkData.amount,
      invoiceNumber: linkData.invoiceNumber,
      email: linkData.email,
      createdAt: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
      isUsed: false,
      isExpired: false,
      agentName: linkData.agentName,
      createdBy: linkData.createdBy || 'admin'
    };

    const docRef = await addDoc(collection(db, 'secureLinks'), linkDoc);
    
    return {
      success: true,
      linkId,
      fullUrl: `${window.location.origin}/card-form?linkId=${linkId}`,
      expiresAt: expiresAt.toISOString()
    };
  } catch (error) {
    console.error('Error creating secure link:', error);
    throw new Error('Failed to create secure link');
  }
};

// Validar enlace seguro
export const validateSecureLink = async (linkId) => {
  try {
    // Buscar el enlace en Firestore
    const q = query(
      collection(db, 'secureLinks'),
      where('linkId', '==', linkId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        isValid: false,
        message: 'Link not found',
        status: 'not_found'
      };
    }

    const linkDoc = querySnapshot.docs[0];
    const linkData = linkDoc.data();
    
    // Verificar si ya fue usado
    if (linkData.isUsed) {
      return {
        isValid: false,
        message: 'This link has already been used',
        status: 'already_used'
      };
    }

    // Verificar si expiró
    const now = new Date();
    const expiresAt = linkData.expiresAt.toDate();
    
    if (now > expiresAt) {
      // Marcar como expirado
      await updateDoc(doc(db, 'secureLinks', linkDoc.id), {
        isExpired: true
      });
      
      return {
        isValid: false,
        message: 'This link has expired',
        status: 'expired'
      };
    }

    return {
      isValid: true,
      message: 'Link is valid',
      status: 'valid',
      linkData: {
        amount: linkData.amount,
        invoiceNumber: linkData.invoiceNumber,
        email: linkData.email,
        expiresAt: expiresAt.toISOString(),
        agentName: linkData.agentName
      }
    };
  } catch (error) {
    console.error('Error validating secure link:', error);
    return {
      isValid: false,
      message: 'Error validating link',
      status: 'error'
    };
  }
};

// Marcar enlace como usado
export const markLinkAsUsed = async (linkId) => {
  try {
    const q = query(
      collection(db, 'secureLinks'),
      where('linkId', '==', linkId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const linkDoc = querySnapshot.docs[0];
      await updateDoc(doc(db, 'secureLinks', linkDoc.id), {
        isUsed: true,
        usedAt: Timestamp.fromDate(new Date())
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error marking link as used:', error);
    return false;
  }
};

// Obtener estadísticas de enlaces
export const getLinkStats = async () => {
  try {
    const q = query(collection(db, 'secureLinks'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const links = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      expiresAt: doc.data().expiresAt.toDate()
    }));

    const now = new Date();
    
    const stats = {
      total: links.length,
      active: links.filter(link => !link.isUsed && !link.isExpired && now < link.expiresAt).length,
      used: links.filter(link => link.isUsed).length,
      expired: links.filter(link => link.isExpired || now > link.expiresAt).length
    };

    return stats;
  } catch (error) {
    console.error('Error getting link stats:', error);
    return { total: 0, active: 0, used: 0, expired: 0 };
  }
};

// Limpiar enlaces expirados (ejecutar periódicamente)
export const cleanupExpiredLinks = async () => {
  try {
    const now = new Date();
    const q = query(
      collection(db, 'secureLinks'),
      where('expiresAt', '<', Timestamp.fromDate(now))
    );
    
    const querySnapshot = await getDocs(q);
    
    const deletePromises = querySnapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
    
    return querySnapshot.size; // Número de enlaces eliminados
  } catch (error) {
    console.error('Error cleaning up expired links:', error);
    return 0;
  }
};

// Obtener todos los enlaces para el dashboard
export const getAllLinks = async () => {
  try {
    const q = query(collection(db, 'secureLinks'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      expiresAt: doc.data().expiresAt.toDate()
    }));
  } catch (error) {
    console.error('Error getting all links:', error);
    return [];
  }
};